// Friends List Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class FriendsList extends HTMLElement {
  constructor() {
    super();
    this.friends = [];
    this.isLoading = true;
    this.currentUser = null;
    this.isLoadingFriends = false; // Prevent multiple simultaneous calls
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.loadFriends();
  }

  disconnectedCallback() {
    // Remove event listeners when component is removed
    if (this.handleSectionShown) {
      document.removeEventListener('section-shown', this.handleSectionShown);
    }
  }

  isVisible() {
    // Check if the parent friends-section is visible
    const friendsSection = document.getElementById('friends-section');
    const sectionVisible = friendsSection && !friendsSection.classList.contains('hidden');
    return sectionVisible;
  }


  render() {
    this.innerHTML = `
      <div class="card friends-list">
        <div class="friends-header">
          ${renderPageTitle('🎪', 'Your Friends', 'h3')}
          <div class="header-actions">
            <button class="btn-icon refresh-btn" id="refreshBtn" title="Refresh">
              🔄
            </button>
          </div>
        </div>

        <div class="friends-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading your friends...</p>
        </div>
      `;
    }

    if (this.friends.length === 0) {
      return `
        <div class="empty-state">
          <span class="empty-state-icon">🎪</span>
          <h4>No friends yet</h4>
          <p>When people accept your friend requests, they'll appear here!</p>
        </div>
      `;
    }

    return `
      <div class="friends-grid">
        ${this.friends.map((friend, index) => `
          <div class="friend-item" data-friend-id="${friend.id}" style="animation-delay: ${index * 0.1}s">
            <div class="friend-info">
              <div class="avatar">
                ${friend.username.charAt(0).toUpperCase()}
              </div>
              <div class="friend-details">
                <h4><a href="/profile/${escapeHtml(friend.username)}" class="friend-link">${escapeHtml(friend.username)}</a></h4>
                <p>${escapeHtml(friend.email)}</p>
                <div class="friendship-date">
                  Friends since ${this.formatDate(friend.friendship_date)}
                </div>
              </div>
            </div>
            <div class="friend-actions">
              <div class="friend-status">
                ✅ Friends
              </div>
              <button 
                class="remove-friend-btn" 
                data-friend-id="${friend.id}"
                data-username="${escapeHtml(friend.username)}"
                title="Remove friend"
              >
                Remove
              </button>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  attachEventListeners() {
    // Listen for section shown events to load friends when Friends section is displayed
    this.handleSectionShown = (event) => {
      if (event.detail.section === 'friends') {
        // Only load friends if we have a current user and API is available
        if (this.currentUser && window.API && this.isAuthenticated()) {
          this.loadFriends();
        }
      }
    };
    document.addEventListener('section-shown', this.handleSectionShown);

    // Refresh button
    const refreshBtn = this.querySelector('#refreshBtn');
    refreshBtn?.addEventListener('click', () => {
      this.loadFriends();
    });

    // Remove friend buttons
    this.addEventListener('click', (e) => {
      if (e.target.closest('.remove-friend-btn')) {
        const btn = e.target.closest('.remove-friend-btn');
        const friendId = btn.dataset.friendId;
        const username = btn.dataset.username;
        this.removeFriend(friendId, username, btn);
      }
    });

    // Listen for friend request events
    document.addEventListener('friend-request-accepted', () => {
      // Refresh friends list when a request is accepted
      setTimeout(() => this.loadFriends(), 500);
    });
  }

  async loadFriends() {
    // Prevent multiple simultaneous calls
    if (this.isLoadingFriends) {
      return;
    }

    // Only load if component is visible
    if (!this.isVisible()) {
      return;
    }

    this.isLoadingFriends = true;
    this.isLoading = true;
    this.updateContent();

    try {
      const response = await window.API.request('/friends');
      this.friends = response.friends || [];

      // Update header with count
      this.updateFriendsCount();
    } catch (error) {
      window.logger.error('Failed to load friends', error, { component: 'friends-list' });
      this.friends = [];
      this.showError('Failed to load friends');
    } finally {
      this.isLoading = false;
      this.isLoadingFriends = false;
      this.updateContent();
    }
  }

  async removeFriend(friendId, username, btn) {
    if (!confirm(`Are you sure you want to remove ${username} as a friend? 😢`)) {
      return;
    }

    const originalContent = btn.innerHTML;

    btn.disabled = true;
    btn.innerHTML = '⏳ Removing...';

    try {
      await window.API.request(`/friends/${friendId}`, {
        method: 'DELETE'
      });

      // Show success message
      this.showSuccess(`${username} has been removed from your friends list`);

      // Remove from local array
      this.friends = this.friends.filter(friend => friend.id !== parseInt(friendId));

      // Update display
      this.updateContent();
      this.updateFriendsCount();

      // Dispatch event
      this.dispatchEvent(new CustomEvent('friend-removed', {
        detail: { friendId, username },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to remove friend', error, { component: 'friends-list', friendId });
      btn.disabled = false;
      btn.innerHTML = originalContent;
      this.showError(error.message || 'Failed to remove friend');
    }
  }

  updateContent() {
    const contentContainer = this.querySelector('.friends-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderContent();
      this.attachEventListeners();
    }
  }

  updateFriendsCount() {
    const header = this.querySelector('.friends-header h3');
    if (header) {
      const countSpan = header.querySelector('.friends-count');
      if (countSpan) {
        countSpan.textContent = this.friends.length;
      } else if (this.friends.length > 0) {
        header.outerHTML = renderPageTitle('🎪', `Your Friends <span class="friends-count">${this.friends.length}</span>`, 'h3');
      } else {
        header.outerHTML = renderPageTitle('🎪', 'Your Friends', 'h3');
      }
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'today';
    } else if (diffInDays === 1) {
      return 'yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  showSuccess(message) {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.textContent = message;
    successDiv.style.position = 'fixed';
    successDiv.style.top = '20px';
    successDiv.style.right = '20px';
    successDiv.style.zIndex = '10000';
    successDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(successDiv);

    setTimeout(() => successDiv.remove(), 4000);
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 4000);
  }


  // Public method to refresh friends
  refresh() {
    this.loadFriends();
  }

  // Public method to add a friend (when request is accepted)
  addFriend(friend) {
    if (!this.friends.find(f => f.id === friend.id)) {
      this.friends.push(friend);
      this.updateContent();
      this.updateFriendsCount();
    }
  }

  isAuthenticated() {
    return this.currentUser !== null;
  }

  // Public method to remove a friend
  removeFriendById(friendId) {
    this.friends = this.friends.filter(friend => friend.id !== parseInt(friendId));
    this.updateContent();
    this.updateFriendsCount();
  }

  // Open the invite friend modal
  openInviteModal() {
    let inviteModal = document.querySelector('invite-friend-modal');
    if (!inviteModal) {
      inviteModal = document.createElement('invite-friend-modal');
      document.body.appendChild(inviteModal);
    }
    inviteModal.open();
  }
}

// Register the custom element
customElements.define('friends-list', FriendsList);
